/**
 * Rate Limit Indicator Component
 * Displays rate limit status and feedback to users
 */

import React from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  LinearProgress,
  Typography,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useRateLimit } from '../hooks/useRateLimit';

interface RateLimitIndicatorProps {
  /** Whether to show the indicator even in normal state */
  alwaysVisible?: boolean;
  /** Position of the indicator */
  position?: 'top' | 'bottom' | 'inline';
  /** Whether the indicator can be dismissed */
  dismissible?: boolean;
  /** Custom styling */
  sx?: any;
}

export const RateLimitIndicator: React.FC<RateLimitIndicatorProps> = ({
  alwaysVisible = false,
  position = 'top',
  dismissible = true,
  sx,
}) => {
  const {
    rateLimitInfo,
    status,
    shouldShowWarning,
    getStatusColor,
    getStatusMessage,
    getTimeUntilResetText,
  } = useRateLimit();

  const [dismissed, setDismissed] = React.useState(false);

  // Don't show if dismissed or if no rate limit info and not always visible
  if (dismissed || (!alwaysVisible && (!rateLimitInfo || status === 'normal'))) {
    return null;
  }

  const handleDismiss = () => {
    if (dismissible && status !== 'exceeded') {
      setDismissed(true);
    }
  };

  const getSeverity = () => {
    switch (status) {
      case 'exceeded':
        return 'error';
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'exceeded':
      case 'critical':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top':
        return {
          position: 'fixed' as const,
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
        };
      case 'bottom':
        return {
          position: 'fixed' as const,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
        };
      default:
        return {};
    }
  };

  return (
    <Collapse in={!dismissed}>
      <Box
        sx={{
          ...getPositionStyles(),
          ...sx,
        }}
      >
        <Alert
          severity={getSeverity()}
          icon={getIcon()}
          action={
            dismissible && status !== 'exceeded' ? (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            ) : null
          }
          sx={{
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <AlertTitle>
            API Rate Limit {status === 'exceeded' ? 'Exceeded' : 'Warning'}
          </AlertTitle>
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {getStatusMessage()}
            </Typography>

            {rateLimitInfo && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* Usage Progress Bar */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Usage
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {rateLimitInfo.used}/{rateLimitInfo.limit}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={rateLimitInfo.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getStatusColor(),
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>

                {/* Status Chips */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${rateLimitInfo.percentage}%`}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                  
                  {status === 'exceeded' && rateLimitInfo.resetTime > 0 && (
                    <Chip
                      label={`Reset in ${getTimeUntilResetText()}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: getStatusColor(),
                        color: getStatusColor(),
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Additional Information */}
            {status === 'exceeded' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                API requests are temporarily blocked. Please wait for the limit to reset.
              </Typography>
            )}
            
            {status === 'critical' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Consider reducing API request frequency to avoid hitting the rate limit.
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    </Collapse>
  );
};

/**
 * Compact rate limit indicator for use in toolbars or status bars
 */
export const CompactRateLimitIndicator: React.FC<{
  sx?: any;
  showLabel?: boolean;
}> = ({ sx, showLabel = true }) => {
  const {
    rateLimitInfo,
    status,
    shouldShowWarning,
    getStatusColor,
  } = useRateLimit();

  if (!shouldShowWarning() && !rateLimitInfo) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
      {rateLimitInfo && (
        <>
          <LinearProgress
            variant="determinate"
            value={rateLimitInfo.percentage}
            sx={{
              width: 60,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getStatusColor(),
                borderRadius: 2,
              },
            }}
          />
          
          {showLabel && (
            <Typography
              variant="caption"
              sx={{
                color: shouldShowWarning() ? getStatusColor() : 'text.secondary',
                fontWeight: shouldShowWarning() ? 'bold' : 'normal',
                minWidth: 35,
              }}
            >
              {rateLimitInfo.percentage}%
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default RateLimitIndicator;